import { Component, ErrorInfo, ReactNode } from 'react';
import { ErrorPage } from '@/pages/ErrorPage';
import { trackBoundaryError } from '@/lib/telemetry/errorTracking';
import { logErrorToOtel } from '@/lib/telemetry/otel';

interface ErrorBoundaryProps {
    children: ReactNode;
    /** Optional custom fallback component */
    fallback?: ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

/**
 * Error Boundary Component
 *
 * Catches unhandled JavaScript errors in child components and displays
 * a user-friendly error page instead of crashing the app.
 *
 * Usage:
 * ```tsx
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 * ```
 *
 * Note: Error boundaries do NOT catch errors in:
 * - Event handlers (use try/catch)
 * - Asynchronous code (use try/catch or error handling hooks)
 * - Server-side rendering
 * - Errors thrown in the error boundary itself
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        // Update state so the next render shows the fallback UI
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Log error to console in development
        console.error('ErrorBoundary caught an error:', error);
        console.error('Component stack:', errorInfo.componentStack);

        // Send error to our backend analytics
        trackBoundaryError(error, errorInfo.componentStack || undefined);

        // Mirror to OpenTelemetry — no-op until VITE_OTEL_ENDPOINT is configured
        logErrorToOtel(error, {
            type: 'react_boundary',
            componentStack: errorInfo.componentStack || undefined,
        });
    }

    handleRetry = () => {
        // Reset error state to attempt re-rendering children
        this.setState({ hasError: false, error: null });
    };

    render() {
        if (this.state.hasError) {
            // If a custom fallback is provided, use it
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // Otherwise, render the default error page
            return (
                <ErrorPage
                    error={this.state.error}
                    onRetry={this.handleRetry}
                />
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
